import { z } from 'zod';

export const PLAY_CLAW_JWT_TEMPLATE = 'play-claw-jwt';
const AUTH_REDIRECT_QUERY_KEY = 'redirect_url';

const ClerkAuthEnvSchema = z.object({
  VITE_AUTH_SITE_URL: z.string().trim().url().optional(),
  VITE_CLERK_SATELLITE_HOSTS: z.string().trim().optional(),
});

type AuthSiteConfig = {
  authSiteUrl: string;
  signInUrl: string;
  signUpUrl: string;
};

type ClerkSatelliteConfig = {
  domain: string;
  signInUrl: string;
  signUpUrl: string;
};

type AuthRoutePath = '/sign-in' | '/sign-up';

const normalizeOptionalEnvValue = (value: string | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  return trimmedValue;
};

const normalizeHostname = (value: string): string => {
  const trimmedValue = value.trim();
  const normalizedValue = trimmedValue.toLowerCase();

  return normalizedValue;
};

const parseSatelliteHosts = (value: string | undefined): string[] => {
  if (!value) {
    return [];
  }

  const rawHosts = value.split(',');
  const normalizedHosts = rawHosts.map((host) => normalizeHostname(host));
  const configuredHosts = normalizedHosts.filter((host) => host.length > 0);
  const uniqueHosts = Array.from(new Set(configuredHosts));

  return uniqueHosts;
};

const parseClerkAuthEnv = () => {
  const rawEnv = {
    VITE_AUTH_SITE_URL: normalizeOptionalEnvValue(import.meta.env.VITE_AUTH_SITE_URL),
    VITE_CLERK_SATELLITE_HOSTS: normalizeOptionalEnvValue(import.meta.env.VITE_CLERK_SATELLITE_HOSTS),
  };
  const parsedEnv = ClerkAuthEnvSchema.parse(rawEnv);

  return parsedEnv;
};

const getCurrentHostname = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const currentHostname = window.location.hostname;
  const normalizedHostname = normalizeHostname(currentHostname);

  return normalizedHostname;
};

const readCurrentUrl = (): URL | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const currentUrl = new URL(window.location.href);

  return currentUrl;
};

const createAbsoluteAppUrl = (pathname: string): string => {
  const currentUrl = readCurrentUrl();

  if (currentUrl === null) {
    return pathname;
  }

  const absoluteUrl = new URL(pathname, currentUrl.origin);
  const resolvedUrl = absoluteUrl.toString();

  return resolvedUrl;
};

const parseRedirectUrl = (value: string | null): URL | null => {
  if (!value) {
    return null;
  }

  try {
    const redirectUrl = new URL(value);

    return redirectUrl;
  } catch {
    return null;
  }
};

const isCurrentOriginRedirectUrl = (candidateUrl: URL): boolean => {
  const currentUrl = readCurrentUrl();

  if (currentUrl === null) {
    return false;
  }

  const isMatchingOrigin = candidateUrl.origin === currentUrl.origin;

  return isMatchingOrigin;
};

const readRequestedRedirectUrl = (): URL | null => {
  const currentUrl = readCurrentUrl();

  if (currentUrl === null) {
    return null;
  }

  const redirectValue = currentUrl.searchParams.get(AUTH_REDIRECT_QUERY_KEY);
  const redirectUrl = parseRedirectUrl(redirectValue);

  if (redirectUrl === null) {
    return null;
  }

  const isAllowedRedirectUrl = isCurrentOriginRedirectUrl(redirectUrl);

  if (!isAllowedRedirectUrl) {
    return null;
  }

  return redirectUrl;
};

const buildRelativeUrl = (url: URL): string => {
  const relativeUrl = `${url.pathname}${url.search}${url.hash}`;

  return relativeUrl;
};

const createAuthSiteUrl = (
  authSiteUrl: string,
  pathname: string,
  redirectUrl?: string | null
): string => {
  const authSiteOrigin = new URL(authSiteUrl);
  const authSitePath = pathname;
  const authSiteUrlWithPath = new URL(authSitePath, authSiteOrigin);

  if (redirectUrl) {
    authSiteUrlWithPath.searchParams.set(AUTH_REDIRECT_QUERY_KEY, redirectUrl);
  }

  const resolvedUrl = authSiteUrlWithPath.toString();

  return resolvedUrl;
};

const buildAuthSiteConfig = (): AuthSiteConfig | null => {
  const parsedEnv = parseClerkAuthEnv();
  const authSiteUrl = parsedEnv.VITE_AUTH_SITE_URL;

  if (!authSiteUrl) {
    return null;
  }

  const signInUrl = createAuthSiteUrl(authSiteUrl, '/sign-in');
  const signUpUrl = createAuthSiteUrl(authSiteUrl, '/sign-up');
  const authSiteConfig = {
    authSiteUrl,
    signInUrl,
    signUpUrl,
  };

  return authSiteConfig;
};

const buildSatelliteConfig = (hostname: string): ClerkSatelliteConfig | null => {
  const parsedEnv = parseClerkAuthEnv();
  const configuredHosts = parseSatelliteHosts(parsedEnv.VITE_CLERK_SATELLITE_HOSTS);
  const isSatelliteHost = configuredHosts.includes(hostname);

  if (!isSatelliteHost) {
    return null;
  }

  const authSiteConfig = buildAuthSiteConfig();

  if (!authSiteConfig) {
    throw new Error('Missing VITE_AUTH_SITE_URL for Clerk satellite host.');
  }

  const satelliteConfig = {
    domain: hostname,
    signInUrl: authSiteConfig.signInUrl,
    signUpUrl: authSiteConfig.signUpUrl,
  };

  return satelliteConfig;
};

export const buildAuthSiteEntryUrl = (
  pathname: AuthRoutePath,
  redirectUrl?: string | null
): string => {
  const authSiteConfig = buildAuthSiteConfig();

  if (authSiteConfig === null) {
    throw new Error('Missing VITE_AUTH_SITE_URL');
  }

  const authSiteEntryUrl = createAuthSiteUrl(authSiteConfig.authSiteUrl, pathname, redirectUrl);

  return authSiteEntryUrl;
};

export const buildLocalAuthPath = (pathname: AuthRoutePath, redirectUrl: string): string => {
  const absoluteAuthUrl = new URL(pathname, createAbsoluteAppUrl('/'));
  absoluteAuthUrl.searchParams.set(AUTH_REDIRECT_QUERY_KEY, redirectUrl);
  const authPath = buildRelativeUrl(absoluteAuthUrl);

  return authPath;
};

export const resolveRequestedRedirectUrl = (fallbackPath: string): string => {
  const requestedRedirectUrl = readRequestedRedirectUrl();

  if (requestedRedirectUrl) {
    return requestedRedirectUrl.toString();
  }

  const fallbackRedirectUrl = createAbsoluteAppUrl(fallbackPath);

  return fallbackRedirectUrl;
};

export const resolveRequestedRedirectPath = (fallbackPath: string): string => {
  const redirectUrl = resolveRequestedRedirectUrl(fallbackPath);
  const parsedRedirectUrl = new URL(redirectUrl, createAbsoluteAppUrl('/'));
  const redirectPath = buildRelativeUrl(parsedRedirectUrl);

  return redirectPath;
};

export const getAuthSiteConfig = (): AuthSiteConfig | null => {
  const authSiteConfig = buildAuthSiteConfig();

  return authSiteConfig;
};

export const getClerkSatelliteConfig = (): ClerkSatelliteConfig | null => {
  const currentHostname = getCurrentHostname();

  if (!currentHostname) {
    return null;
  }

  const satelliteConfig = buildSatelliteConfig(currentHostname);

  return satelliteConfig;
};

export const isClerkSatelliteHost = (): boolean => {
  const satelliteConfig = getClerkSatelliteConfig();
  const isSatelliteHost = satelliteConfig !== null;

  return isSatelliteHost;
};
